import { FC, MutableRefObject, useRef, useState } from "react";
import { UseFormReturnType, useForm } from "@mantine/form";
import { useSnackbar } from "notistack";
import { z } from "zod";
import { usePermissions } from "@/hooks/usePermissions";
import {
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";
import {
  Grid,
  TextInput,
  DateInput,
  TextAreaInput,
  Button,
} from "@monolith-forensics/monolith-ui";
import ButtonMenu from "@/components/Forms/components/ButtonMenu.js";
import LocationCocSelector from "@/components/Forms/components/Selectors/LocationCocSelector";
import { getIpAddress } from "@/api";
import { db_timestamp } from "@/utils/date-format";
import validateAsync from "@/utils/ZodHelpers/validateAsync";
import EvidenceCocAPI from "@/api/evidence/coc";
import { createSignature } from "../utils";
import type { BaseCocModalProps, CocSignatureObject } from "../Coc.types";
import type { Location } from "@/pages/Locations/LocationsSection";
import { SignatureField, SignatureFieldRef } from "@/Features/Signatures";

export interface IntakeFormData {
  custody_to: Location | null;
  custody_from: string;
  coc_intake_timestamp: string;
  location_received: string;
  reason: string;
}

// Props for embedded mode (used in DefaultEvidenceForm)
interface EmbeddedIntakeCocFormProps {
  form: UseFormReturnType<any>;
  receivedBySig: MutableRefObject<SignatureFieldRef>;
  receivedFromSig: MutableRefObject<SignatureFieldRef>;
  // Child evidence customization props
  isChildEvidence?: boolean;
  childEvidenceLabels?: {
    receivedByLabel?: string;
    receivedFromLabel?: string;
    timestampLabel?: string;
    receivedBySignatureLabel?: string;
    receivedFromSignatureLabel?: string;
  };
}

// Props for modal mode (used in IntakeCocModal)
interface ModalIntakeCocFormProps {
  width?: number | undefined;
  onSubmit: () => void;
  onCancel: () => void;
  defaultInfo: BaseCocModalProps["defaultInfo"];
  defaultFormData?: IntakeFormData;
  buttonProps?: { cancelText?: string; submitText?: string };
}

type IntakeCocFormProps = EmbeddedIntakeCocFormProps | ModalIntakeCocFormProps;

// Type guards to determine which mode we're in
const isEmbeddedMode = (
  props: IntakeCocFormProps
): props is EmbeddedIntakeCocFormProps => {
  return "form" in props;
};

const isModalMode = (
  props: IntakeCocFormProps
): props is ModalIntakeCocFormProps => {
  return "onSubmit" in props;
};

// Base schema for intake fields - all optional by default
const baseIntakeSchema = z.object({
  custody_to: z.any().nullable().optional(),
  custody_from: z.string().nullable().optional(),
  coc_intake_timestamp: z.string().nullable().optional(),
  location_received: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
});

// Required schema when intake fields are dirty
const requiredIntakeSchema = z.object({
  custody_to: z
    .object(
      {
        location_id: z.number(),
        location_name: z.string(),
      },
      {
        required_error: "Received By is required",
      }
    )
    .refine((val) => val !== null && val !== undefined, {
      message: "Received By is required",
    }),
  custody_from: z
    .string({
      required_error: "Received From is required",
    })
    .min(1, { message: "Received From is required" }),
  coc_intake_timestamp: z
    .string({
      required_error: "Intake Timestamp is required",
    })
    .min(1, { message: "Intake Timestamp is required" }),
  location_received: z.string().optional(),
  reason: z.string().optional(),
});

// Function to determine if intake fields are dirty (have been modified)
export const checkIntakeFieldsDirty = (
  formData: any,
  prepopulatedValues?: { custody_from?: string | null; reason?: string | null }
): boolean => {
  // A field with a prepopulated default only counts as dirty once the user
  // changes it away from that default.
  const isDirty = (value: unknown, prepopulated?: string | null) =>
    Boolean(value) && value !== "" && value !== prepopulated;

  return (
    isDirty(formData.custody_to) ||
    isDirty(formData.coc_intake_timestamp) ||
    isDirty(formData.location_received) ||
    isDirty(formData.custody_from, prepopulatedValues?.custody_from) ||
    isDirty(formData.reason, prepopulatedValues?.reason)
  );
};

// Function to get appropriate schema based on dirty state
export const getIntakeValidationSchema = (isDirty: boolean) => {
  return isDirty ? requiredIntakeSchema : baseIntakeSchema;
};

const IntakeCocForm: FC<IntakeCocFormProps> = (props) => {
  // Modal mode state and handlers
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const modalReceivedBySig = useRef<SignatureFieldRef>();
  const modalReceivedFromSig = useRef<SignatureFieldRef>();

  const modalForm = useForm<IntakeFormData>({
    mode: "uncontrolled",
    initialValues:
      isModalMode(props) && props.defaultFormData
        ? props.defaultFormData
        : {
            custody_to: null,
            custody_from: "",
            coc_intake_timestamp: new Date().toISOString(),
            location_received: "",
            reason: "",
          },
  });

  // Modal mode submission handler
  const handleModalSubmit = async () => {
    if (!isModalMode(props)) return;

    const formData = modalForm.getValues();

    const validationResult = await validateAsync(requiredIntakeSchema)(
      formData
    );
    if (validationResult?.hasErrors) {
      console.error("Validation Errors", validationResult?.errors);
      modalForm.setErrors(validationResult?.errors);
      return;
    }

    setIsLoading(true);
    try {
      const ipAddress = await getIpAddress();

      const signature: CocSignatureObject = {
        custody_to_sig: createSignature(
          { current: modalReceivedBySig.current! },
          ipAddress,
          currentUser
        ),
        custody_from_sig: createSignature(
          { current: modalReceivedFromSig.current! },
          ipAddress,
          currentUser
        ),
      };

      const requestData = {
        evidence_id: props.defaultInfo.evidence_id,
        custody_to: formData.custody_to?.location_id || 0,
        custody_from: formData.custody_from,
        coc_intake_timestamp: db_timestamp(formData.coc_intake_timestamp, true),
        location_received: formData.location_received,
        reason: formData.reason || "Evidence Intake",
        coc_type: "Intake",
        signature: JSON.stringify(signature),
        entered_by: currentUser.full_name,
      };

      await EvidenceCocAPI.createIntakeDetails(requestData);

      enqueueSnackbar("Entered Evidence Intake Details", {
        variant: "success",
      });

      modalForm.reset();
      props.onSubmit?.();
    } catch (error) {
      console.error("Error creating intake details:", error);
      enqueueSnackbar("Failed to create intake details", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalCancel = () => {
    if (!isModalMode(props)) return;
    modalForm.reset();
    props.onCancel();
  };

  // Embedded mode - just return the form fields
  if (isEmbeddedMode(props)) {
    const embeddedForm = props.form;
    const { isChildEvidence = false, childEvidenceLabels = {} } = props;

    // Default labels
    const defaultLabels = {
      receivedByLabel: isChildEvidence ? "Item Removed By" : "Received By",
      receivedFromLabel: isChildEvidence ? "Removed From" : "Received From",
      timestampLabel: isChildEvidence
        ? "Item Removal Timestamp"
        : "Intake Timestamp",
      receivedBySignatureLabel: isChildEvidence
        ? "Item Removed By Signature"
        : "Received By Signature",
      receivedFromSignatureLabel: isChildEvidence
        ? "Removed From Signature"
        : "Received From Signature",
    };

    // Merge with custom labels
    const labels = { ...defaultLabels, ...childEvidenceLabels };

    return (
      <Grid col={2}>
        <LocationCocSelector
          label={labels.receivedByLabel}
          placeholder="Select location"
          key={embeddedForm.key("custody_to")}
          {...embeddedForm.getInputProps("custody_to")}
          description={"Person or location receiving item"}
        />

        <TextInput
          size="sm"
          variant="outlined"
          label={labels.receivedFromLabel}
          placeholder={
            isChildEvidence
              ? "Parent evidence number..."
              : "John Smith, FedEx: tracking 12345..."
          }
          description={
            isChildEvidence
              ? "Parent evidence this item was removed from"
              : "Person, location, or method from which the item was received"
          }
          disabled={isChildEvidence}
          key={embeddedForm.key("custody_from")}
          {...embeddedForm.getInputProps("custody_from")}
        />

        <div>
          <SignatureField
            sigFieldRef={props.receivedBySig}
            label={labels.receivedBySignatureLabel}
            useESignature={true}
          />
        </div>

        <div>
          <SignatureField
            sigFieldRef={props.receivedFromSig}
            label={labels.receivedFromSignatureLabel}
          />
        </div>

        <DateInput
          size="sm"
          variant="outlined"
          label={labels.timestampLabel}
          description={
            isChildEvidence
              ? "Date and time the item was removed from parent"
              : "Date and time intake"
          }
          enableCalendar
          arrow
          clearable={true}
          format="YYYY-MM-DD HH:mm:ss"
          key={embeddedForm.key("coc_intake_timestamp")}
          {...embeddedForm.getInputProps("coc_intake_timestamp")}
        />

        <TextInput
          size="sm"
          variant="outlined"
          label="Location Received"
          placeholder="Address, Front Office, etc..."
          key={embeddedForm.key("location_received")}
          {...embeddedForm.getInputProps("location_received")}
          description={
            "Person or location where item is currently being stored"
          }
        />

        <div style={{ gridColumn: "1 / -1" }}>
          <TextAreaInput
            size="sm"
            variant="outlined"
            label="Notes"
            placeholder={
              isChildEvidence
                ? "Enter any additional notes about removing this item from parent"
                : "Enter any additional notes on this item"
            }
            description={
              isChildEvidence
                ? "Any additional notes about this removal"
                : "Any additional notes on this item"
            }
            minRows={3}
            key={embeddedForm.key("reason")}
            {...embeddedForm.getInputProps("reason")}
          />
        </div>
      </Grid>
    );
  }

  // Modal mode - wrap with form wrapper and buttons
  const { width, buttonProps = {} } = props;
  const { cancelText = "Cancel", submitText = "Create Intake Details" } =
    buttonProps;

  return (
    <FormWrapper width={width}>
      <FormTitle>Create Evidence Intake Details</FormTitle>
      <div style={{ margin: "20px 0px" }}>
        <Grid col={2}>
          <LocationCocSelector
            label="Received By"
            placeholder="Select location"
            required
            key={modalForm.key("custody_to")}
            {...modalForm.getInputProps("custody_to")}
          />

          <TextInput
            size="sm"
            variant="outlined"
            label="Received From"
            placeholder="John Smith, FedEx: tracking 12345..."
            required
            key={modalForm.key("custody_from")}
            {...modalForm.getInputProps("custody_from")}
          />

          <div>
            <SignatureField
              sigFieldRef={modalReceivedBySig}
              label="Received By Signature"
              useESignature={true}
            />
          </div>

          <div>
            <SignatureField
              sigFieldRef={modalReceivedFromSig}
              label="Received From Signature"
            />
          </div>

          <DateInput
            size="sm"
            variant="outlined"
            label="Intake Timestamp"
            enableCalendar
            arrow
            format="YYYY-MM-DD HH:mm:ss"
            required
            key={modalForm.key("coc_intake_timestamp")}
            {...modalForm.getInputProps("coc_intake_timestamp")}
          />

          <TextInput
            size="sm"
            variant="outlined"
            label="Location Received"
            placeholder="Address, Front Office, etc..."
            key={modalForm.key("location_received")}
            {...modalForm.getInputProps("location_received")}
          />

          <div style={{ gridColumn: "1 / -1" }}>
            <TextAreaInput
              size="sm"
              variant="outlined"
              label="Notes"
              minRows={3}
              key={modalForm.key("reason")}
              {...modalForm.getInputProps("reason")}
            />
          </div>
        </Grid>

        <ButtonMenu>
          <Button
            size="xs"
            variant="subtle"
            onClick={handleModalCancel}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            size="xs"
            color="primary"
            variant="contained"
            onClick={handleModalSubmit}
            disabled={isLoading}
            loading={isLoading}
          >
            {submitText}
          </Button>
        </ButtonMenu>
      </div>
    </FormWrapper>
  );
};

export default IntakeCocForm;
